import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class LocalStorageCacheService {
  private cache: Map<string, any> = new Map<string, any>();
  private inFlight: Map<string, Observable<any>> = new Map();

  constructor(@Inject(PLATFORM_ID) private platformId: any) {}

  // --- Existing API (no TTL, backward-compatible) ---

  setItem(key: string, value: any): void {
    if (isPlatformBrowser(this.platformId)) {
      this.cache.set(key, value);
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  getItem(key: string): any {
    if (isPlatformBrowser(this.platformId)) {
      if (this.cache.has(key)) {
        return this.cache.get(key);
      }
      const storedValue = localStorage.getItem(key);
      if (storedValue) {
        const parsedValue = JSON.parse(storedValue);
        this.cache.set(key, parsedValue);
        return parsedValue;
      }
    }
    return null;
  }

  removeItem(key: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.cache.delete(key);
      this.inFlight.delete(key);
      localStorage.removeItem(key);
    }
  }

  clear(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.cache.clear();
      this.inFlight.clear();
      localStorage.clear();
    }
  }

  // --- TTL-aware API ---

  setWithTTL<T>(key: string, data: T): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const entry: CacheEntry<T> = { data, timestamp: Date.now() };
    this.cache.set(key, entry);
    try {
      localStorage.setItem(key, JSON.stringify(entry));
    } catch {}
  }

  getWithTTL<T>(key: string, ttlMs: number): T | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    if (this.cache.has(key)) {
      const entry = this.cache.get(key) as CacheEntry<T>;
      if (Date.now() - entry.timestamp <= ttlMs) return entry.data;
      this.cache.delete(key);
    }

    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const entry: CacheEntry<T> = JSON.parse(raw);
      if (Date.now() - entry.timestamp > ttlMs) {
        localStorage.removeItem(key);
        return null;
      }
      this.cache.set(key, entry);
      return entry.data;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Wraps an HTTP Observable with localStorage caching.
   * Returns cached data immediately if valid, and refreshes in background.
   * If no cache, returns the HTTP call directly.
   */
  cachedGet<T>(key: string, ttlMs: number, source$: Observable<T>): Observable<T> {
    const cached = this.getWithTTL<T>(key, ttlMs);

    if (cached) {
      if (!this.inFlight.has(key)) {
        const refresh$ = source$.pipe(
          tap(data => this.setWithTTL(key, data)),
          shareReplay(1),
        );
        this.inFlight.set(key, refresh$);
        refresh$.subscribe({ complete: () => this.inFlight.delete(key) });
      }
      return of(cached);
    }

    const request$ = source$.pipe(
      tap(data => this.setWithTTL(key, data)),
      shareReplay(1),
    );
    this.inFlight.set(key, request$);
    return request$;
  }
}
